import { BrowserTracing, Replay, init } from "@sentry/react"
import config from "@/v2-status-client/config"

// Sample rates are env-driven so operators can dial them up/down per
// environment without a redeploy. Defaults are conservative because
// the public status page sees a lot of unauthenticated traffic and
// we don't want to blow through Sentry quota. Override via
// window.env.TRACES_SAMPLE_RATE / REPLAYS_SESSION_SAMPLE_RATE /
// REPLAYS_ERROR_SAMPLE_RATE.
const env = typeof window === "undefined" ? {} : (window.env ?? {})
const parseRate = (raw: unknown, fallback: number) => {
  const n = typeof raw === "string" ? Number(raw) : Number.NaN
  return Number.isFinite(n) && n >= 0 && n <= 1 ? n : fallback
}
const TRACE_SAMPLE_RATE = parseRate(env.TRACES_SAMPLE_RATE, 0.05)
const REPLAY_SESSION_SAMPLE_RATE = parseRate(env.REPLAYS_SESSION_SAMPLE_RATE, 0)
const REPLAY_ERROR_SAMPLE_RATE = parseRate(env.REPLAYS_ERROR_SAMPLE_RATE, 1)

if (config.isInstrumentationEnabled) {
  init({
    dsn: config.sentryDsn,
    dist: "placepoint-status",
    release: config.version,
    environment: config.envName,
    beforeSend(event, hint) {
      const e = hint.originalException
      if (!e) return event
      if (e instanceof Error) {
        // Drop transient network blips -- the page polls /public/status
        // every minute, so a single dropped fetch is expected and not
        // actionable. The footer already surfaces the failed-poll
        // state to the user.
        if (/failed to fetch/i.test(e.message)) return null
        if (/load failed/i.test(e.message)) return null
        if (/network error/i.test(e.message)) return null
        if (/aborterror/i.test(e.message)) return null
        if (/abortederror/i.test(e.message)) return null
      }
      return event
    },
    tracesSampler(ctx) {
      const { request } = ctx
      if (request?.url) {
        const { url } = request
        // Azure App Service health probe + bot traffic. No value
        // sampling these into traces.
        if (url.includes("/__hs")) return 0
        if (url.includes("/robots933456.txt")) return 0
        if (url.includes("/robots.txt")) return 0
        if (url.includes("/favicon")) return 0
        if (url.includes("/env.js")) return 0
      }
      return TRACE_SAMPLE_RATE
    },
    tracePropagationTargets: [
      "localhost",
      /placepoint/,
      /v2-status-api/,
      /^\/public/,
    ],
    replaysSessionSampleRate: REPLAY_SESSION_SAMPLE_RATE,
    replaysOnErrorSampleRate: REPLAY_ERROR_SAMPLE_RATE,
    integrations: [
      new BrowserTracing({
        // The status page is a single route -- no react-router and no
        // history pushes -- so don't pay for the listener.
        startTransactionOnLocationChange: false,
        shouldCreateSpanForRequest(url) {
          if (url.includes("/__hs")) return false
          if (url.includes("/robots933456.txt")) return false
          if (url.includes("/robots.txt")) return false
          if (url.includes("/favicon")) return false
          if (url.includes("/env.js")) return false
          // Only the API poll is worth measuring. Static asset
          // fetches are already tracked by browser performance APIs.
          if (url.includes("/public/")) return true
          return false
        },
      }),
      // Session Replay is opt-in (default 0% baseline + 100% on
      // error). Public surface so we mask all text + media to avoid
      // capturing anything unexpected from a customer's screen.
      new Replay({
        maskAllText: true,
        blockAllMedia: true,
      }),
    ],
  })
}
